import { workoutDisplayDigits as displayDigits } from '../../utils/workoutNumberFormat';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Map, Marker, GeoJSONSource } from 'maplibre-gl';
import type { GpsMapProps } from './GpsMap';
import 'maplibre-gl/dist/maplibre-gl.css';
import './TerrainGpsMap.css';

const imageryCredit = '© Esri, Maxar, Earthstar Geographics and the GIS User Community';
const tileUrl = (style: GpsMapProps['mapStyle']) => {
  const service = style === 'street' ? 'World_Street_Map' : style === 'light' ? 'World_Topo_Map' : style === 'dark' ? 'Canvas/World_Dark_Gray_Base' : 'World_Imagery';
  return `https://services.arcgisonline.com/ArcGIS/rest/services/${service}/MapServer/tile/{z}/{y}/{x}`;
};
const line = (points: [number, number][]) => ({ type: 'Feature' as const, properties: {}, geometry: { type: 'LineString' as const, coordinates: points.map(([lat, lng]) => [lng, lat]) } });

export default function TerrainGpsMap(props: GpsMapProps & { onUnavailable: () => void }) {
  const { t } = useTranslation();
  const container = useRef<HTMLDivElement>(null);
  const mapRef = useRef<Map | null>(null);
  const latest = useRef(props);
  const initialStyle = useRef(props.mapStyle);
  latest.current = props;
  const [ready, setReady] = useState(false);
  const [is3d, setIs3d] = useState(false);
  const [terrainFailed, setTerrainFailed] = useState(false);
  const [library, setLibrary] = useState<typeof import('maplibre-gl') | null>(null);

  useEffect(() => {
    let disposed = false;
    let observer: ResizeObserver | undefined;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    import('maplibre-gl').then(gl => {
      if (disposed || !container.current) return;
      const map = new gl.Map({
        container: container.current, center: [latest.current.gpsRoute[0][1], latest.current.gpsRoute[0][0]], zoom: 11, pitch: 0, maxPitch: 75,
        attributionControl: false,
        style: {
          version: 8,
          sources: {
            base: { type: 'raster', tiles: [tileUrl(latest.current.mapStyle)], tileSize: 256, maxzoom: 19, attribution: imageryCredit },
            elevation: { type: 'raster-dem', tiles: ['https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png'], encoding: 'terrarium', tileSize: 256, maxzoom: 15, attribution: '<a href="https://github.com/tilezen/joerd/blob/master/docs/attribution.md" target="_blank" rel="noopener noreferrer">Terrain © Mapzen</a>' },
            route: { type: 'geojson', data: line(latest.current.gpsRoute) },
            selection: { type: 'geojson', data: { type: 'FeatureCollection', features: [] } },
          },
          layers: [
            { id: 'base', type: 'raster', source: 'base' },
            { id: 'route', type: 'line', source: 'route', layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#36b9ff', 'line-width': 4 } },
            { id: 'selection', type: 'line', source: 'selection', layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#ff3186', 'line-width': 6 } },
          ],
        },
      });
      mapRef.current = map;
      setLibrary(gl);
      map.addControl(new gl.NavigationControl({ visualizePitch: true }), 'top-left');
      map.touchZoomRotate.enable();
      map.touchPitch.enable();
      map.scrollZoom.disable();
      map.on('webglcontextlost', () => latest.current.onUnavailable());
      let baseErrors = 0;
      let baseAttempt = 0;
      map.on('error', event => {
        const source = (event as unknown as { sourceId?: string }).sourceId;
        if (source === 'elevation') {
          map.setTerrain(null);
          setTerrainFailed(true);
          setIs3d(false);
          map.setPitch(0);
        } else if (source === 'base' && ++baseErrors >= 3) {
          baseErrors = 0;
          const base = map.getSource('base') as import('maplibre-gl').RasterTileSource;
          if (baseAttempt++ === 0) base.setTiles([tileUrl(latest.current.mapStyle).replace('services.arcgisonline.com', 'server.arcgisonline.com')]);
          else if (baseAttempt === 2) {
            map.removeLayer('base');
            map.removeSource('base');
            map.addSource('base', { type: 'raster', tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'], tileSize: 256, maxzoom: 19, attribution: '© OpenStreetMap contributors' });
            map.addLayer({ id: 'base', type: 'raster', source: 'base' }, 'route');
          } else latest.current.onUnavailable();
        }
      });
      timeout = setTimeout(() => { if (!map.loaded()) latest.current.onUnavailable(); }, 25000);
      map.on('load', () => {
        clearTimeout(timeout);
        if (!disposed) setReady(true);
      });
      observer = new ResizeObserver(() => map.resize());
      observer.observe(container.current);
    }).catch(() => { if (!disposed) latest.current.onUnavailable(); });
    return () => {
      disposed = true;
      clearTimeout(timeout);
      observer?.disconnect();
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || !library) return;
    (map.getSource('route') as GeoJSONSource).setData(line(props.gpsRoute));
    const bounds = new library.LngLatBounds();
    props.gpsRoute.forEach(([lat, lng]) => bounds.extend([lng, lat]));
    map.fitBounds(bounds, { padding: 42, maxZoom: 16, pitch: terrainFailed ? 0 : map.getPitch(), duration: 0 });
    const markers: Marker[] = [];
    [props.gpsRoute[0], props.gpsRoute[props.gpsRoute.length - 1]].forEach((p, i) => {
      const el = document.createElement('div');
      el.className = `wm-map-marker wm-map-marker--${i ? 'finish' : 'start'}`;
      el.textContent = i ? 'F' : 'S';
      markers.push(new library.Marker({ element: el }).setLngLat([p[1], p[0]]).addTo(map));
    });
    return () => markers.forEach(marker => marker.remove());
  }, [ready, library, props.gpsRoute, props.fitKey, terrainFailed]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const points = props.gpsSelection ?? [];
    (map.getSource('selection') as GeoJSONSource).setData(points.length >= 2 ? line(points) : { type: 'FeatureCollection', features: [] });
    map.setPaintProperty('route', 'line-opacity', points.length >= 2 ? 0.4 : 1);
  }, [ready, props.gpsSelection]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || !library || !props.focusToken || !props.gpsSelection?.length) return;
    const bounds = new library.LngLatBounds();
    props.gpsSelection.forEach(([lat, lng]) => bounds.extend([lng, lat]));
    map.fitBounds(bounds, { padding: 48, maxZoom: 17, duration: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 500 });
  }, [ready, library, props.focusToken]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || !library || !props.hoverPosition) return;
    const el = document.createElement('div');
    el.className = 'wm-map-marker wm-map-marker--hover';
    el.textContent = 'H';
    const marker = new library.Marker({ element: el }).setLngLat([props.hoverPosition[1], props.hoverPosition[0]]).addTo(map);
    return () => { marker.remove(); };
  }, [ready, library, props.hoverPosition]);

  useEffect(() => {
    if (!ready) return;
    if (props.mapStyle === initialStyle.current) return;
    initialStyle.current = props.mapStyle;
    const source = mapRef.current?.getSource('base') as import('maplibre-gl').RasterTileSource | undefined;
    source?.setTiles([tileUrl(props.mapStyle)]);
  }, [ready, props.mapStyle]);

  const toggle = () => {
    const map = mapRef.current;
    if (!map) return;
    map.setTerrain(is3d ? null : { source: 'elevation', exaggeration: 1 });
    map.easeTo({ pitch: is3d ? 0 : 55, duration: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 350 });
    setIs3d(!is3d);
  };
  return <div className={`wm-map-wrap wm-terrain-map ltr-island ${props.className ?? ''}`}>
    <div ref={container} className="wm-terrain-map__canvas" />
    <button className="wm-terrain-map__toggle" type="button" disabled={!ready || terrainFailed} onClick={toggle} aria-pressed={is3d} aria-label={t('workoutDetail.map.terrain3d')}>{displayDigits(is3d ? '2D' : '3D')}</button>
    {terrainFailed && <span className="wm-terrain-map__notice" role="status">{t('workoutDetail.map.terrainUnavailable')}</span>}
  </div>;
}
